import jsyaml from "js-yaml";

import { REGISTRY_DISPLAY_PREFIX } from "./ImageSelector";

const parseYamlObject = (yamlText) => {
  const parsed = jsyaml.load(yamlText);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Config file must contain a top-level YAML object.");
  }
  return parsed;
};

const parsePositiveInt = (value) => {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!/^[0-9]+$/.test(trimmed)) return null;
    const parsed = Number(trimmed);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
};

const getDatasetName = (dataset) => {
  if (!dataset || typeof dataset !== "object") return null;
  return dataset.dataset_name || null;
};

const joinCommands = (commands) => {
  if (typeof commands === "string") return commands;
  if (!Array.isArray(commands)) return "";
  return commands.filter((command) => typeof command === "string").join("\n\n");
};

const normalizeImportedImageRef = (image) => {
  const trimmed = typeof image === "string" ? image.trim() : "";
  if (trimmed.startsWith(REGISTRY_DISPLAY_PREFIX)) {
    return trimmed.slice(REGISTRY_DISPLAY_PREFIX.length);
  }
  return trimmed;
};

export const importExperimentConfig = ({
  yamlText,
  groups,
  availableDatasets,
}) => {
  const parsed = parseYamlObject(yamlText.replace(/\r/g, ""));

  const jobLabel = typeof parsed.job_label === "string" ? parsed.job_label : "";
  const workspaceLabel =
    typeof parsed.workspace_label === "string" ? parsed.workspace_label : "";

  const selectedGroup = (groups || []).find((group) => {
    return group.name === parsed.group_name;
  });

  let walltimeHours = null;
  if (parsed.walltime_seconds != null) {
    const seconds = parsePositiveInt(parsed.walltime_seconds);
    if (seconds !== null)
      walltimeHours = Math.max(1, Math.ceil(seconds / 3600));
  } else if (parsed.walltime_hours != null) {
    walltimeHours = parsePositiveInt(parsed.walltime_hours);
  }

  const matchedDatasets = [];
  const datasets = Array.isArray(parsed.datasets) ? parsed.datasets : [];
  datasets.forEach((dataset) => {
    if (!dataset || typeof dataset !== "object" || Array.isArray(dataset))
      throw new Error("Invalid dataset reference.");
    const datasetName = getDatasetName(dataset);
    const match = (availableDatasets || []).find((item) =>
      dataset.dataset_id
        ? item.id === dataset.dataset_id
        : item.title === datasetName,
    );
    if (!match)
      throw new Error(
        `Dataset ${datasetName || dataset.dataset_id || "reference"} is unavailable.`,
      );
    if (datasetName && datasetName !== match.title)
      throw new Error("Dataset ID and name do not match.");
    if (matchedDatasets.some((item) => item.id === match.id))
      throw new Error("Duplicate dataset selection.");
    matchedDatasets.push(match);
  });

  return {
    values: {
      imageRef: normalizeImportedImageRef(parsed.image),
      experimentType: parsed.experiment_type,
      jobLabel: jobLabel,
      workspaceName: workspaceLabel,
      selectedGroup: selectedGroup,
      walltimeHours: walltimeHours,
      selectedDatasets: matchedDatasets,
      commands: joinCommands(parsed.commands),
      gpuConfig: {
        gpuProduct:
          typeof parsed.gpu_product === "string"
            ? parsed.gpu_product.trim()
            : "",
        gpuCount: parsed.gpu_count ?? "",
        nodeCount: parsed.node_count ?? "",
        cpuCores: parsed.cpu_request_cores ?? "",
        memory: parsed.memory_request_gb ?? "",
        burstBufferEnabled: parsed.burst_storage_enabled === true,
      },
      cpuConfig: {
        nodeCount: parsed.node_count ?? "",
        cpuCores: parsed.cpu_request_cores ?? "",
        memory: parsed.memory_request_gb ?? "",
      },
    },
  };
};

export const importJobConfig = (args) => {
  const imported = importExperimentConfig(args);
  if (imported.values.experimentType !== "job") {
    throw new Error("This uploaded config is not a job configuration.");
  }
  return imported;
};

export const importWorkspaceConfig = (args) => {
  const imported = importExperimentConfig(args);
  if (imported.values.experimentType !== "workspace") {
    throw new Error("This uploaded config is not a workspace configuration.");
  }
  return imported;
};
